import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Icon } from '@/components/ui/icon';
import { toast } from "@/hooks/errors/use-toast";
import { supabase } from '@/integrations/supabase/client';
import AuthLayout from '@/components/auth/AuthLayout';
import type { EmailOtpType } from '@supabase/supabase-js';

const AuthConfirm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleTokenExchange = async () => {
      const token_hash = searchParams.get('token_hash');
      const type = searchParams.get('type') as EmailOtpType | null;
      const next = searchParams.get('next') || (type === 'signup' ? '/welcome' : '/signin');

      console.log('Auth confirm params:', { token_hash: token_hash?.substring(0, 20) + '...', type, next });

      if (token_hash && type) {
        try {
          const { data, error } = await supabase.auth.verifyOtp({
            type,
            token_hash,
          });

          if (error) {
            console.error('Token verification error:', error);
            toast({
              title: "Verification Failed",
              description: error.message || "Unable to verify your request. Please try again.",
              variant: "error",
            });
            
            // Redirect based on type
            if (type === 'recovery') {
              navigate('/forgot-password');
            } else {
              navigate('/signin');
            }
            return;
          }

          if (data.user) {
            console.log('Token verification successful for:', data.user.email);
            
            // Show success message
            if (type === 'recovery') {
              toast({
                title: "Reset Link Verified",
                description: "You can now set your new password.",
                variant: "success",
              });
            } else if (type === 'signup') {
              toast({
                title: "Email Verified",
                description: "Your email has been verified successfully.",
                variant: "success",
              });
            }

            // Redirect to the next page
            navigate(next);
          } else {
            throw new Error('No user data returned after verification');
          }

        } catch (error: any) {
          console.error('Auth confirmation error:', error);
          toast({
            title: "Verification Error",
            description: "Something went wrong during verification. Please try again.",
            variant: "error",
          });
          
          // Redirect based on type
          if (type === 'recovery') {
            navigate('/forgot-password');
          } else {
            navigate('/signin');
          }
        }
      } else {
        console.error('Missing required parameters:', { token_hash: !!token_hash, type });
        toast({
          title: "Invalid Link",
          description: "This verification link is invalid or malformed. Please request a new one.",
          variant: "error",
        });
        navigate('/signin');
      }
    };

    handleTokenExchange();
  }, [searchParams, navigate]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <AuthLayout
        title="Verifying..."
        description="Please wait while we verify your request"
      >
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <Icon name="Loader2" className="h-8 w-8 animate-spin text-accent" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Processing your verification link...
          </p>
        </div>
      </AuthLayout>
    </motion.div>
  );
};

export default AuthConfirm; 